'use client';

import { useCallback, useEffect, useState } from 'react';

import { type AppToastPayload, type AppToastSeverity, subscribeAppToast } from '@/lib/ui/globalAppToast';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import type { SnackbarCloseReason } from '@mui/material/Snackbar';

import styles from './GlobalAppToastHost.module.scss';

const AUTO_HIDE_MS = 5_000;

export const GlobalAppToastHost = () => {
    const [toast, setToast] = useState<(AppToastPayload & { key: number }) | null>(null);

    useEffect(() => {
        return subscribeAppToast((payload) => {
            setToast({
                ...payload,
                key: Date.now(),
            });
        });
    }, []);

    const handleClose = useCallback((_: unknown, reason: SnackbarCloseReason) => {
        if (reason === 'clickaway') {
            return;
        }

        setToast(null);
    }, []);

    const dismissAlert = useCallback(() => {
        setToast(null);
    }, []);

    const severity: AppToastSeverity = toast?.severity ?? 'error';

    return (
        <Snackbar
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            autoHideDuration={AUTO_HIDE_MS}
            key={toast?.key ?? 'closed'}
            onClose={handleClose}
            open={Boolean(toast)}
        >
            <Alert className={styles.alert} onClose={dismissAlert} severity={severity} variant="filled">
                {toast?.message ?? ''}
            </Alert>
        </Snackbar>
    );
};
